import { addYears } from 'date-fns';
import { useEffect, useLayoutEffect, type FC } from 'react';
import { useNavigate } from 'react-router';
import { useMemoOne } from 'use-memo-one';
import { useOptionalExchangeRatesCurrent } from 'src/apis/exchangerate-service/use-exchange-rates-current';
import type { AuctionsParams } from 'src/apis/monolith/types';
import { useAuctions } from 'src/apis/monolith/use-auctions';
import { TableActions } from 'src/components/buttons-and-actions/table-actions';
import { Card } from 'src/components/data-display/card';
import { FormatDate } from 'src/components/data-display/format-date';
import { FormatValue } from 'src/components/data-display/format-value';
import { Table } from 'src/components/data-display/smart-table';
import { Td, Th } from 'src/components/data-display/table';
import { getPaginationSchema } from 'src/components/form/zod-schemas';
import { Link } from 'src/components/navigation/link';
import { PageSpinner } from 'src/components/spinner-container';
import { useNow } from 'src/hooks/use-now';
import {
  stringifySearchParams,
  usePageParamsFromSchema,
} from 'src/hooks/use-page-params-from-schema';
import type { SavedReferrer } from 'src/hooks/use-referrer';
import { PeriodType } from 'src/pages/reporting/auctions/components/period-type';
import { Capacity } from 'src/pages/reporting/capacity';
import {
  PriceInEURMWhTd,
  PriceInEURMWhTh,
} from 'src/pages/transport/auctions/overview/price-in-eur-mwh-col';
import type { RunningAuctionsPageParams } from 'src/pages/transport/auctions/overview/use-page-params';
import { AuctionStatusWithCountdown } from 'src/pages/transport/network-points/details/point-details/auctions-now';

type AuctionLiveAtNetworkPointProps = {
  savedReferrer: SavedReferrer;
  networkPointId: number;
  pageParams: AuctionLivePageParams;
};

export const AuctionLiveTable: FC<AuctionLiveAtNetworkPointProps> = ({
  savedReferrer,
  networkPointId,
  pageParams,
}) => {
  // always start on the first page: reset pagination on mount (if needed)
  const navigate = useNavigate();
  useLayoutEffect(() => {
    if (pageParams.value.start !== 0) {
      navigate(
        { search: pageParams.toSearchParams({ start: 0 }) },
        { replace: true }
      );
    }
    // oxlint-disable-next-line exhaustive-deps
  }, []);

  const now = useNow();
  const exchangeRates = useOptionalExchangeRatesCurrent();

  // Should fetch all auctions for the network point
  const auctionsParams = useMemoOne<AuctionsParams>(
    () => ({
      periodType: [
        'DAY',
        'WITHINDAY',
        'MONTH',
        'QUARTER',
        'YEAR',
        'REST_OF_YEAR',
      ],
      // Long-term can be PUBLISHED, OPEN, or CLOSED; short-term should be OPEN
      phase: ['PUBLISHED', 'OPEN', 'CLOSED'],
      networkPoint: [networkPointId],
      runtimeFrom: new Date(now).toISOString(),
      runtimeTo: addYears(now, 20).toISOString(),
      start: pageParams.value.start,
      pageSize: pageParams.value.pageSize,
    }),
    [networkPointId, now, pageParams.value.pageSize, pageParams.value.start]
  );
  const auctions = useAuctions({ params: auctionsParams });

  useEffect(() => {
    auctions.execute(auctionsParams);
    // oxlint-disable-next-line exhaustive-deps
  }, [auctionsParams]);

  if (!auctions.response || !exchangeRates.loaded)
    return (
      <Card>
        <PageSpinner />
      </Card>
    );

  return (
    <Table
      data-testid="live-auctions-table"
      data={auctions.response.data.data}
      pending={auctions.pending}
      paginated={{
        ...auctions.response.data,
        perPage: pageParams.value.pageSize,
        paramPrefix: 'auctionLive',
      }}
      cols={[
        {
          key: 'capacityCategory',
          head: <Th>Category</Th>,
          body: (data) => (
            <Td data-testid={`capacityCategory-${data.id}`}>
              <Capacity value={data.capacityCategory0} />
              {data.networkPoint.bundled && data.capacityCategory1 && (
                <>
                  {' / '}
                  <Capacity value={data.capacityCategory1} />
                </>
              )}
            </Td>
          ),
        },
        {
          key: 'periodType',
          width: '6rem',
          head: <Th>Product</Th>,
          body: (data) => (
            <Td align="center" data-testid={`periodType-${data.id}`}>
              <PeriodType value={data.periodType} />
            </Td>
          ),
        },
        {
          key: 'runtime',
          width: '13rem',
          head: <Th>Runtime</Th>,
          body: (data) => (
            <Td data-testid={`runtime-${data.id}`}>
              <FormatDate value={data.runtime.start} type="date-time" />
              {' - '}
              <FormatDate value={data.runtime.end} type="date-time" />
            </Td>
          ),
        },
        {
          key: 'amount',
          head: <Th>Amount</Th>,
          body: (data) => (
            <Td data-testid={`amount-${data.id}`} align="right">
              <FormatValue
                value={data.marketable.value.value}
                label={data.marketable.unit}
                smallLabel
                stacked
              />
            </Td>
          ),
        },
        {
          width: 2,
          key: 'startingPrice',
          head: <Th>Starting Price</Th>,
          body: (data) => (
            <Td data-testid={`startingPrice-${data.id}`} align="right">
              <FormatValue
                type="4-decimals"
                value={data.startingPrice[0].value.value}
                label={data.startingPrice[0].unit.label}
                smallLabel
                stacked
              />
              {data.startingPrice[1] && (
                <>
                  {' '}
                  <em>or</em>
                  <br />
                  <FormatValue
                    type="4-decimals"
                    value={data.startingPrice[1].value.value}
                    label={data.startingPrice[1].unit.label}
                    smallLabel
                    stacked
                  />
                </>
              )}
            </Td>
          ),
        },
        {
          key: 'startingPriceEURMWh',
          width: 2,
          head: <PriceInEURMWhTh />,
          body: (data) => (
            <PriceInEURMWhTd auction={data} exchangeRates={exchangeRates} />
          ),
        },
        {
          width: 2,
          key: 'status',
          head: <Th>Status</Th>,
          body: (data) => (
            <Td data-testid={`status-${data.id}`}>
              <AuctionStatusWithCountdown auction={data} />
            </Td>
          ),
        },
        {
          width: '8rem',
          key: 'actions',
          head: <Th align="center">Actions</Th>,
          body: (auction) => (
            <Td align="center" data-testid={`actions-${auction.id}`}>
              <TableActions>
                <Link
                  {...savedReferrer.withReferrerLinkProps(
                    auction.algorithmType === 'LONG_TERM'
                      ? `/transport/auctions/${auction.id}`
                      : {
                          pathname: '/transport/auctions/list',
                          search: stringifySearchParams({
                            slot: auction.slot,
                            phase: auction.phase,
                            periodType: auction.periodType,
                            auctionId: auction.id.toString(),
                            'runtime-from': auction.runtime.start,
                            'runtime-to': auction.runtime.end,
                            favoritesOnly: false,
                          } satisfies Partial<
                            RunningAuctionsPageParams['value']
                          >),
                        }
                  )}
                >
                  Bid Here
                </Link>
              </TableActions>
            </Td>
          ),
        },
      ]}
      empty={{
        label: 'No auctions without bids available.',
      }}
    />
  );
};

const auctionLiveSchema = getPaginationSchema();

export function useAuctionLivePageParams() {
  return usePageParamsFromSchema(auctionLiveSchema, {
    groupPrefix: 'auctionLive',
  });
}

export type AuctionLivePageParams = ReturnType<typeof useAuctionLivePageParams>;
